(() => {
  const API_NAME = 'NordwegPredictiveSearchLoader';
  const CONFIG_SELECTOR = 'script[data-predictive-search-loader]';
  const INTENT_SELECTOR = 'details-modal.header__search, predictive-search';
  const INPUT_SELECTOR = 'predictive-search input[type="search"]';
  const mobileMediaQuery = window.matchMedia('(max-width: 989px)');

  if (window[API_NAME]?.refresh) {
    window[API_NAME].refresh();
    return;
  }

  const assetPromises = new Map();
  const queuedInputs = new Set();
  let primaryUrl = '';
  let mobileUrl = '';
  let loadPromise = null;
  let mobilePromise = null;
  let loadedMobileUrl = '';

  function readConfiguration() {
    const configs = [...document.querySelectorAll(CONFIG_SELECTOR)];
    const config = configs[configs.length - 1];
    primaryUrl = config?.dataset.predictiveSearchScript || primaryUrl;
    mobileUrl = config?.dataset.mobilePredictiveSearchScript || mobileUrl;
    return config;
  }

  function loadScript(url, runtime) {
    if (assetPromises.has(url)) return assetPromises.get(url);

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.predictiveSearchRuntime = runtime;

      const settle = (event) => {
        script.removeEventListener('load', settle);
        script.removeEventListener('error', settle);
        if (event.type === 'load') {
          resolve();
          return;
        }
        script.remove();
        reject(new Error(`Failed to load predictive search runtime: ${url}`));
      };

      script.addEventListener('load', settle);
      script.addEventListener('error', settle);
      (document.head || document.documentElement).appendChild(script);
    }).catch((error) => {
      assetPromises.delete(url);
      throw error;
    });

    assetPromises.set(url, promise);
    return promise;
  }

  function isReady() {
    return Boolean(customElements.get('predictive-search'));
  }

  function isMobileReady() {
    return !mobileUrl || loadedMobileUrl === mobileUrl;
  }

  function shouldDeferInput(input) {
    return queuedInputs.has(input);
  }

  function loadMobileRuntime() {
    if (isMobileReady()) return Promise.resolve(true);
    if (mobilePromise) return mobilePromise;

    const requestedUrl = mobileUrl;
    mobilePromise = loadScript(requestedUrl, 'mobile')
      .then(() => {
        loadedMobileUrl = requestedUrl;
        return true;
      })
      .catch((error) => {
        console.error(error);
        return false;
      })
      .finally(() => {
        mobilePromise = null;
      });
    return mobilePromise;
  }

  function replayQueuedInputs() {
    const inputs = [...queuedInputs];
    queuedInputs.clear();
    inputs.forEach((input) => {
      if (!input.isConnected || !input.closest('predictive-search')) return;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
  }

  async function loadRuntime() {
    if (!primaryUrl) throw new Error('Predictive search runtime URL is unavailable.');

    const primaryRuntime = isReady()
      ? Promise.resolve()
      : loadScript(primaryUrl, 'primary');
    const mobileRuntime = mobileUrl
      ? loadMobileRuntime()
      : Promise.resolve(true);

    if (!isReady()) {
      await primaryRuntime;
      if (!isReady()) throw new Error('Predictive search runtime loaded without registering its element.');
    }

    if (mobileMediaQuery.matches) await mobileRuntime;
    replayQueuedInputs();
    if (!mobileMediaQuery.matches) await mobileRuntime;
    return customElements.get('predictive-search');
  }

  function load() {
    readConfiguration();
    if (loadPromise) return loadPromise;
    loadPromise = loadRuntime().finally(() => {
      loadPromise = null;
    });
    return loadPromise;
  }

  function targetElement(target) {
    return target instanceof Element ? target : null;
  }

  function warmFromIntent(event) {
    if (
      !targetElement(event.target)?.closest(INTENT_SELECTOR)
      || (isReady() && isMobileReady())
    ) return;
    load().catch(() => false);
  }

  function queueColdInput(event) {
    const input = targetElement(event.target)?.closest(INPUT_SELECTOR);
    if (!input) return;
    if (isReady()) {
      if (mobileMediaQuery.matches && !isMobileReady()) {
        queuedInputs.add(input);
        load().catch(() => false);
      }
      return;
    }
    queuedInputs.add(input);
    load().catch(() => false);
  }

  function refresh() {
    const config = readConfiguration();
    if (config?.dataset.designMode === 'true') load().catch(() => false);
  }

  const api = { isReady, load, refresh, shouldDeferInput };
  window[API_NAME] = api;

  document.addEventListener('pointerover', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('pointerdown', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('touchstart', warmFromIntent, { capture: true, passive: true });
  document.addEventListener('focusin', warmFromIntent, true);
  document.addEventListener('input', queueColdInput, true);

  refresh();
})();
